import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { useAuth, isScintillaStaff, isInstaller } from '@/contexts/AuthContext';
import { ChevronLeft, MapPin, Calendar, Package, ClipboardCheck, Upload, Check, XCircle, AlertCircle, FileText, Camera, Loader2, Play } from 'lucide-react';

const CATEGORY_LABELS = {
  installazione: 'Installazione',
  connessione: 'Pratiche di connessione',
  conformita: 'Conformità',
  consegna: 'Consegna e fine lavori',
};

const STATUS_STYLES = {
  da_pianificare: { label: 'Da pianificare', variant: 'secondary' },
  pianificato: { label: 'Pianificato', variant: 'default' },
  in_corso: { label: 'In corso', variant: 'default' },
  completato: { label: 'Completato', variant: 'outline' },
  verificato: { label: 'Verificato', variant: 'outline' },
  annullato: { label: 'Annullato', variant: 'destructive' },
};

const ITEM_STATUS_BADGE = {
  missing: { label: 'Mancante', variant: 'destructive' },
  uploaded: { label: 'Caricato', variant: 'default' },
  verified: { label: 'Verificato', variant: 'outline' },
  rejected: { label: 'Rifiutato', variant: 'destructive' },
};

export default function InstallerInterventoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = isScintillaStaff(user);
  const [inst, setInst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({}); // { key: true }
  const [technicalNotes, setTechnicalNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const fileInputs = useRef({}); // ref per input file per key

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.getInstallation(id);
      setInst(d);
      setTechnicalNotes(d.technical_notes || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento intervento');
      navigate(-1);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const fileToDataUrl = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const handleFilePick = async (itemKey, file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File troppo grande (max 20MB). Comprimi immagini o dividi PDF.');
      return;
    }
    setUploading(prev => ({ ...prev, [itemKey]: true }));
    const tId = toast.loading('Caricamento file in corso…');
    try {
      const dataUrl = await fileToDataUrl(file);
      const res = await api.uploadChecklistFile(id, itemKey, { data_url: dataUrl, file_name: file.name });
      toast.success(`${file.name} caricato`, { id: tId });
      // Refresh checklist item locale
      setInst(prev => ({
        ...prev,
        checklist: (prev.checklist || []).map(it => it.key === itemKey ? res.item : it),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore upload', { id: tId });
    } finally {
      setUploading(prev => { const n = { ...prev }; delete n[itemKey]; return n; });
    }
  };

  const handleVerify = async (itemKey, verified, reason = '') => {
    try {
      const res = await api.verifyChecklistItem(id, itemKey, { verified, reject_reason: reason });
      toast.success(verified ? 'Verificato' : 'Rifiutato');
      setInst(prev => ({
        ...prev,
        checklist: (prev.checklist || []).map(it => it.key === itemKey ? res.item : it),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.updateInstallation(id, { technical_notes: technicalNotes });
      toast.success('Note salvate');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally { setSavingNotes(false); }
  };

  const handleChangeStatus = async (newStatus) => {
    if (!window.confirm(`Confermi il cambio di stato a "${newStatus.replace('_', ' ')}"?`)) return;
    try {
      const upd = await api.updateInstallation(id, { status: newStatus });
      setInst(prev => ({ ...prev, ...upd }));
      toast.success('Stato aggiornato');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  if (loading || !inst) {
    return <div className="p-10 text-center text-sm text-muted-foreground">Caricamento intervento…</div>;
  }

  const cust = inst.customer_snapshot || {};
  const custName = cust.customer_type === 'azienda'
    ? cust.company_name
    : `${cust.first_name || ''} ${cust.last_name || ''}`.trim();
  const st = STATUS_STYLES[inst.status] || { label: inst.status, variant: 'secondary' };

  const checklist = inst.checklist || [];
  const grouped = checklist.reduce((acc, it) => {
    acc[it.category] = acc[it.category] || [];
    acc[it.category].push(it);
    return acc;
  }, {});
  const requiredCount = checklist.filter(it => it.required).length;
  const doneRequired = checklist.filter(it => it.required && (it.status === 'uploaded' || it.status === 'verified')).length;
  const canMarkCompleted = inst.status === 'in_corso' && doneRequired === requiredCount;

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ChevronLeft className="size-4 mr-1" />Indietro</Button>
        <div className="flex-1" />
        <Badge variant={st.variant} data-testid="intervento-status">{st.label}</Badge>
      </div>

      <PageHeader
        title={`Intervento ${inst.number || ''}`}
        description={`Cliente: ${custName || '—'} · ${inst.site_city}, ${inst.site_province}`}
        actions={
          <div className="flex gap-2">
            {!isStaff && isInstaller(user) && inst.status === 'pianificato' && (
              <Button size="sm" onClick={() => handleChangeStatus('in_corso')} data-testid="btn-start">
                <Play className="size-3.5 mr-1" />Inizia intervento
              </Button>
            )}
            {!isStaff && isInstaller(user) && canMarkCompleted && (
              <Button size="sm" onClick={() => handleChangeStatus('completato')} data-testid="btn-complete">
                <Check className="size-3.5 mr-1" />Marca completato
              </Button>
            )}
            {isStaff && inst.status === 'completato' && (
              <Button size="sm" onClick={() => handleChangeStatus('verificato')} data-testid="btn-verify-all">
                <ClipboardCheck className="size-3.5 mr-1" />Marca verificato
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="size-3.5" />Indirizzo</div>
          <div className="mt-1 text-sm">{inst.site_address || '—'}</div>
          <div className="text-xs text-muted-foreground">{inst.site_cap} {inst.site_city} ({inst.site_province})</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="size-3.5" />Data pianificata</div>
          <div className="mt-1 text-sm">{inst.planned_date ? new Date(inst.planned_date).toLocaleDateString('it-IT') : 'Da pianificare'}</div>
          {inst.completed_date && <div className="text-xs text-muted-foreground">Completato: {new Date(inst.completed_date).toLocaleDateString('it-IT')}</div>}
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Package className="size-3.5" />Prodotti</div>
          <ul className="mt-1 text-xs space-y-0.5">
            {(inst.products_snapshot || []).slice(0, 3).map((p, i) => (
              <li key={i} className="truncate">• {p.brand} {p.model} ({p.quantity})</li>
            ))}
            {(inst.products_snapshot || []).length > 3 && <li className="text-muted-foreground">+{inst.products_snapshot.length - 3} altri</li>}
          </ul>
        </CardContent></Card>
      </div>

      {/* Progresso checklist */}
      <Card className="mb-4">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="size-4 text-primary" />
            <div className="flex-1">
              <div className="text-sm font-medium">Checklist documentale</div>
              <div className="text-xs text-muted-foreground">{doneRequired}/{requiredCount} voci obbligatorie completate</div>
            </div>
            <div className="w-32 h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${requiredCount ? Math.round(100 * doneRequired / requiredCount) : 0}%` }} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sezioni checklist per categoria */}
      {Object.entries(grouped).map(([cat, items]) => (
        <Card key={cat} className="mb-4">
          <CardContent className="pt-4">
            <div className="text-sm font-semibold mb-3">{CATEGORY_LABELS[cat] || cat}</div>
            <div className="space-y-2">
              {items.map(item => {
                const badge = ITEM_STATUS_BADGE[item.status] || ITEM_STATUS_BADGE.missing;
                const accept = item.file_type === 'pdf' ? '.pdf' : item.file_type === 'foto' ? 'image/*' : 'image/*,.pdf';
                return (
                  <div key={item.key} className="border rounded-lg p-3" data-testid={`checklist-item-${item.key}`}>
                    <div className="flex flex-wrap items-start gap-3">
                      {item.file_type === 'pdf' ? <FileText className="size-4 mt-0.5 text-muted-foreground" /> : <Camera className="size-4 mt-0.5 text-muted-foreground" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                          {item.label}
                          {item.required && <Badge variant="outline" className="text-[10px]">Obbligatorio</Badge>}
                          <Badge variant={badge.variant} className="text-[10px]">{badge.label}</Badge>
                        </div>
                        {item.file_url && (
                          <div className="text-xs text-muted-foreground mt-1">
                            <a href={item.file_url} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid={`file-link-${item.key}`}>
                              📎 {item.file_name || 'Visualizza file caricato'}
                            </a>
                            {item.uploaded_by_name && <span className="ml-2">di {item.uploaded_by_name}</span>}
                          </div>
                        )}
                        {item.reject_reason && (
                          <div className="text-xs text-destructive mt-1 flex items-center gap-1">
                            <AlertCircle className="size-3" />Rifiutato: {item.reject_reason}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {/* Upload button (per installer o staff Scintilla) */}
                        <input
                          ref={el => { fileInputs.current[item.key] = el; }}
                          type="file"
                          accept={accept}
                          className="hidden"
                          onChange={e => handleFilePick(item.key, e.target.files?.[0])}
                          data-testid={`input-file-${item.key}`}
                        />
                        <Button
                          size="sm" variant="outline"
                          onClick={() => fileInputs.current[item.key]?.click()}
                          disabled={!!uploading[item.key] || item.status === 'verified'}
                          data-testid={`btn-upload-${item.key}`}
                        >
                          {uploading[item.key] ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
                          <span className="ml-1">{item.file_url ? 'Sostituisci' : 'Carica'}</span>
                        </Button>
                        {isStaff && item.status === 'uploaded' && (
                          <>
                            <Button size="sm" variant="ghost" title="Verifica" onClick={() => handleVerify(item.key, true)} data-testid={`btn-verify-${item.key}`}>
                              <Check className="size-3.5 text-green-500" />
                            </Button>
                            <Button size="sm" variant="ghost" title="Rifiuta" onClick={() => {
                              const r = window.prompt('Motivo del rifiuto:');
                              if (r !== null) handleVerify(item.key, false, r);
                            }} data-testid={`btn-reject-${item.key}`}>
                              <XCircle className="size-3.5 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Note tecniche */}
      <Card className="mb-4">
        <CardContent className="pt-4 space-y-2">
          <div className="text-sm font-semibold">Note tecniche</div>
          <Textarea value={technicalNotes} onChange={e => setTechnicalNotes(e.target.value)} placeholder="Annotazioni sull'intervento (es. dimensioni cavi, posizionamento, criticità…)" rows={4} data-testid="technical-notes-textarea" />
          <div className="flex justify-end">
            <Button size="sm" onClick={handleSaveNotes} disabled={savingNotes} data-testid="btn-save-notes">
              {savingNotes ? <Loader2 className="size-3.5 mr-1 animate-spin" /> : null}Salva note
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info compenso */}
      {(inst.compensation_amount > 0 || isStaff) && (
        <Card className="mb-4">
          <CardContent className="pt-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-muted-foreground">Compenso installatore</div>
              <div className="text-lg font-semibold tabular-nums">€ {Number(inst.compensation_amount || 0).toFixed(2)}</div>
            </div>
            <Badge variant={inst.compensation_status === 'pagato' ? 'outline' : 'default'}>
              {inst.compensation_status === 'pagato' ? 'Pagato' : 'Da pagare'}
            </Badge>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
